// Per-case Communication Panel: Chat + Alerts tabs with rich text, attachments and @mentions
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import portalApi from '../lib/api';
import { toast } from 'sonner';
import {
  MessageSquare, Bell, Send, Paperclip, Bold, Italic, List, X, Loader2,
  Plus, Download, ChevronDown, ChevronRight, CheckCircle2, Image as ImageIcon
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];
const STATUSES = ['Open', 'In Progress', 'Resolved', 'Closed'];
const priorityColor = {
  Low: 'bg-slate-100 text-slate-700', Medium: 'bg-sky-100 text-sky-700',
  High: 'bg-amber-100 text-amber-800', Urgent: 'bg-red-100 text-red-700',
};
const statusColor = {
  Open: 'bg-emerald-100 text-emerald-700', 'In Progress': 'bg-sky-100 text-sky-700',
  Resolved: 'bg-violet-100 text-violet-700', Closed: 'bg-slate-200 text-slate-600',
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

// ---- Rich text editor (minimal, no deps) + @mentions ----
function RichComposer({ users, onSend, sending, placeholder, caseId, dataTestPrefix }) {
  const editorRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [mentionState, setMentionState] = useState(null); // {query, results}
  const mentionIdsRef = useRef(new Set());

  const exec = (cmd) => { document.execCommand(cmd, false, null); editorRef.current?.focus(); };

  const handleInput = () => {
    const sel = window.getSelection();
    if (!sel || !sel.focusNode) { setMentionState(null); return; }
    const text = sel.focusNode.textContent || '';
    const offset = sel.focusOffset;
    const upto = text.slice(0, offset);
    const m = upto.match(/@([\w.\-]*)$/);
    if (m) {
      const q = m[1].toLowerCase();
      const results = users.filter(u => u.name.toLowerCase().includes(q)).slice(0, 6);
      setMentionState({ query: q, results });
    } else {
      setMentionState(null);
    }
  };

  const insertMention = (user) => {
    mentionIdsRef.current.add(user.id);
    const sel = window.getSelection();
    if (sel && sel.focusNode) {
      const node = sel.focusNode;
      const offset = sel.focusOffset;
      const text = node.textContent || '';
      const upto = text.slice(0, offset);
      const rest = text.slice(offset);
      const newUpto = upto.replace(/@([\w.\-]*)$/, '');
      node.textContent = newUpto;
      const chip = document.createElement('span');
      chip.setAttribute('data-mention-id', user.id);
      chip.setAttribute('contenteditable', 'false');
      chip.className = 'mention-chip';
      chip.textContent = `@${user.name}`;
      const space = document.createTextNode('\u00A0' + rest);
      const parent = node.parentNode;
      parent.insertBefore(chip, node.nextSibling);
      parent.insertBefore(space, chip.nextSibling);
    }
    setMentionState(null);
    editorRef.current?.focus();
  };

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const meta = await portalApi.uploadCaseAttachment(caseId, file);
        setAttachments((prev) => [...prev, meta]);
      }
    } catch (e) { toast.error(e.message || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleDrop = (e) => { e.preventDefault(); uploadFiles(Array.from(e.dataTransfer.files || [])); };

  const collectMentionIds = () => {
    const ids = new Set();
    editorRef.current?.querySelectorAll('[data-mention-id]').forEach((el) => ids.add(el.getAttribute('data-mention-id')));
    return Array.from(ids);
  };

  const submit = async () => {
    const html = editorRef.current?.innerHTML?.trim() || '';
    const text = editorRef.current?.innerText?.trim() || '';
    if (!text && attachments.length === 0) return;
    const mentions = collectMentionIds();
    await onSend({ body_html: html, text, mentions, attachments });
    if (editorRef.current) editorRef.current.innerHTML = '';
    setAttachments([]);
    mentionIdsRef.current = new Set();
  };

  return (
    <div className="border border-slate-200 rounded-xl bg-white" onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-100">
        <button type="button" onClick={() => exec('bold')} className="p-1.5 rounded hover:bg-slate-100" data-testid={`${dataTestPrefix}-bold`}><Bold className="h-4 w-4 text-slate-600" /></button>
        <button type="button" onClick={() => exec('italic')} className="p-1.5 rounded hover:bg-slate-100" data-testid={`${dataTestPrefix}-italic`}><Italic className="h-4 w-4 text-slate-600" /></button>
        <button type="button" onClick={() => exec('insertUnorderedList')} className="p-1.5 rounded hover:bg-slate-100" data-testid={`${dataTestPrefix}-list`}><List className="h-4 w-4 text-slate-600" /></button>
        <label className="p-1.5 rounded hover:bg-slate-100 cursor-pointer" data-testid={`${dataTestPrefix}-attach`}>
          <Paperclip className="h-4 w-4 text-slate-600" />
          <input type="file" multiple hidden onChange={(e) => uploadFiles(Array.from(e.target.files || []))} />
        </label>
        {uploading && <Loader2 className="h-4 w-4 animate-spin text-sky-500 ml-1" />}
      </div>
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          data-testid={`${dataTestPrefix}-editor`}
          className="min-h-[64px] max-h-40 overflow-y-auto px-3 py-2 text-sm text-slate-800 focus:outline-none prose-sm"
          data-placeholder={placeholder}
          suppressContentEditableWarning
        />
        {mentionState && mentionState.results.length > 0 && (
          <div className="absolute z-30 left-3 bottom-2 bg-white border border-slate-200 rounded-lg shadow-lg w-56 max-h-48 overflow-y-auto" data-testid={`${dataTestPrefix}-mention-list`}>
            {mentionState.results.map((u) => (
              <button key={u.id} type="button" onClick={() => insertMention(u)} className="w-full text-left px-3 py-2 hover:bg-sky-50 text-sm flex items-center justify-between">
                <span className="text-slate-800">{u.name}</span>
                <span className="text-xs text-slate-400 capitalize">{(u.role || '').replace('_', ' ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {attachments.length > 0 && (
        <div className="px-3 pb-2 flex flex-wrap gap-2">
          {attachments.map((a) => (
            <span key={a.id} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs rounded px-2 py-1">
              {a.is_image ? <ImageIcon className="h-3 w-3" /> : <Paperclip className="h-3 w-3" />}
              {a.filename}
              <button type="button" onClick={() => setAttachments((p) => p.filter((x) => x.id !== a.id))}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex justify-end px-2 pb-2">
        <Button size="sm" onClick={submit} disabled={sending || uploading} data-testid={`${dataTestPrefix}-send`}>
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Send</>}
        </Button>
      </div>
    </div>
  );
}

function AttachmentChips({ caseId, attachments }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {attachments.map((a) => (
        a.is_image ? (
          <a key={a.id} href={portalApi.getAttachmentUrl(caseId, a.storage_path, a.filename)} target="_blank" rel="noreferrer">
            <img src={portalApi.getAttachmentUrl(caseId, a.storage_path, a.filename)} alt={a.filename} className="h-20 w-20 object-cover rounded-lg border border-slate-200" />
          </a>
        ) : (
          <a key={a.id} href={portalApi.getAttachmentUrl(caseId, a.storage_path, a.filename)} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs rounded px-2 py-1">
            <Download className="h-3 w-3" /> {a.filename}
          </a>
        )
      ))}
    </div>
  );
}

const Msg = ({ html, text }) => (
  html ? <div className="text-sm text-slate-700 chat-html" dangerouslySetInnerHTML={{ __html: html }} />
       : <p className="text-sm text-slate-700 whitespace-pre-wrap">{text}</p>
);

export function CommunicationPanel({ caseId, athenaId, caseManagerId }) {
  const { user, hasPermission } = useAuth();
  const [tab, setTab] = useState('chat');
  const [users, setUsers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const canClose = user && (['admin', 'ls_admin'].includes(user.role) || caseManagerId === user.id);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, n, a] = await Promise.all([
        portalApi.getMentionableUsers(caseId).catch(() => []),
        portalApi.getNotes(caseId).catch(() => []),
        portalApi.getAlerts(caseId).catch(() => []),
      ]);
      setUsers(u); setNotes(n); setAlerts(a);
    } finally { setLoading(false); }
  }, [caseId]);

  // Silent refresh (no spinner) so expanded alert cards keep their state
  const refreshAlerts = useCallback(async () => {
    try { setAlerts(await portalApi.getAlerts(caseId)); } catch { /* ignore */ }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const sendChat = async ({ body_html, text, mentions, attachments }) => {
    setSending(true);
    try {
      await portalApi.addNote(caseId, { text, body_html, mentions, attachments });
      const n = await portalApi.getNotes(caseId);
      setNotes(n);
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  return (
    <div className="w-full xl:w-[400px] xl:flex-shrink-0" data-testid="communication-panel">
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden xl:sticky xl:top-6">
        <div className="flex border-b border-slate-100">
          <button onClick={() => setTab('chat')} data-testid="comm-tab-chat"
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium ${tab === 'chat' ? 'text-sky-600 border-b-2 border-sky-500 bg-sky-50/50' : 'text-slate-500 hover:text-slate-700'}`}>
            <MessageSquare className="h-4 w-4" /> Chat
          </button>
          <button onClick={() => setTab('alerts')} data-testid="comm-tab-alerts"
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium ${tab === 'alerts' ? 'text-sky-600 border-b-2 border-sky-500 bg-sky-50/50' : 'text-slate-500 hover:text-slate-700'}`}>
            <Bell className="h-4 w-4" /> Alerts
            {alerts.filter(a => a.status !== 'Closed').length > 0 && (
              <span className="ml-1 text-xs bg-sky-500 text-white rounded-full px-1.5">{alerts.filter(a => a.status !== 'Closed').length}</span>
            )}
          </button>
        </div>

        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>
        ) : tab === 'chat' ? (
          <ChatTab caseId={caseId} notes={notes} users={users} sendChat={sendChat} sending={sending} />
        ) : (
          <AlertsTab caseId={caseId} alerts={alerts} users={users} canClose={canClose} reload={refreshAlerts} />
        )}
      </div>
    </div>
  );
}

function ChatTab({ caseId, notes, users, sendChat, sending }) {
  const ordered = [...notes].reverse(); // oldest first
  return (
    <div className="flex flex-col" style={{ maxHeight: '70vh' }}>
      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-history">
        {ordered.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No messages yet. Start the conversation.</p>}
        {ordered.map((n) => (
          <div key={n.id} className="flex gap-3" data-testid="chat-message">
            <div className="h-8 w-8 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
              {(n.author_name || '?').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-medium text-slate-900">{n.author_name}</span>
                <span className="text-xs text-slate-400">{fmtDate(n.created_at)}</span>
              </div>
              <Msg html={n.body_html} text={n.text} />
              <AttachmentChips caseId={caseId} attachments={n.attachments} />
            </div>
          </div>
        ))}
      </div>
      <div className="p-3 border-t border-slate-100">
        <RichComposer users={users} onSend={sendChat} sending={sending} placeholder="Write a message… use @ to mention" caseId={caseId} dataTestPrefix="chat" />
      </div>
    </div>
  );
}

function AlertsTab({ caseId, alerts, users, canClose, reload }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', priority: 'Medium', assigned_user_ids: [] });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const createAlert = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      await portalApi.createAlert(caseId, { ...form, mentions: form.assigned_user_ids });
      toast.success('Alert created');
      setForm({ title: '', description: '', priority: 'Medium', assigned_user_ids: [] });
      setCreating(false);
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const changeStatus = async (alert, status) => {
    try {
      await portalApi.updateAlert(caseId, alert.id, { status });
      toast.success(`Alert ${status}`);
      reload();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="flex flex-col" style={{ maxHeight: '70vh' }}>
      <div className="p-3 border-b border-slate-100">
        {!creating ? (
          <Button size="sm" variant="outline" className="w-full" onClick={() => setCreating(true)} data-testid="new-alert-btn">
            <Plus className="h-4 w-4 mr-1" /> New Alert
          </Button>
        ) : (
          <div className="space-y-2 border border-slate-200 rounded-xl p-3">
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="alert-title-input" />
            <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 resize-none" rows={2} data-testid="alert-desc-input" />
            <div className="flex gap-2">
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="flex-1" data-testid="alert-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.assigned_user_ids[0] || ''} onValueChange={(v) => setForm({ ...form, assigned_user_ids: v ? [v] : [] })}>
                <SelectTrigger className="flex-1" data-testid="alert-assign-select"><SelectValue placeholder="Assign to" /></SelectTrigger>
                <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
              <Button size="sm" onClick={createAlert} disabled={saving} data-testid="save-alert-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="alerts-history">
        {alerts.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No alerts for this case.</p>}
        {alerts.map((a) => (
          <div key={a.id} className="border border-slate-200 rounded-xl" data-testid="alert-card">
            <button className="w-full text-left p-3" onClick={() => setExpanded(expanded === a.id ? null : a.id)}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900">{a.title}</span>
                    <Badge className={priorityColor[a.priority]}>{a.priority}</Badge>
                    <Badge className={statusColor[a.status]}>{a.status}</Badge>
                  </div>
                  {a.assigned_user_names?.length > 0 && (
                    <p className="text-xs text-slate-500 mt-1">Assigned: {a.assigned_user_names.join(', ')}</p>
                  )}
                </div>
                {expanded === a.id ? <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />}
              </div>
            </button>
            {expanded === a.id && (
              <AlertDetail caseId={caseId} alert={a} users={users} canClose={canClose} reload={reload} changeStatus={changeStatus} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertDetail({ caseId, alert, users, canClose, reload, changeStatus }) {
  const [sending, setSending] = useState(false);
  const addComment = async ({ body_html, text, mentions, attachments }) => {
    setSending(true);
    try {
      await portalApi.addAlertComment(caseId, alert.id, { text, body_html, mentions, attachments });
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };
  return (
    <div className="px-3 pb-3 border-t border-slate-100 pt-3 space-y-3">
      {alert.description && <p className="text-sm text-slate-600">{alert.description}</p>}
      <AttachmentChips caseId={caseId} attachments={alert.attachments} />
      <div className="flex items-center gap-2 flex-wrap">
        {STATUSES.map((s) => {
          const isClose = s === 'Closed';
          const disabled = isClose && !canClose;
          return (
            <button key={s} disabled={disabled || alert.status === s}
              onClick={() => changeStatus(alert, s)}
              title={disabled ? 'Only Admin, LS Admin, or assigned Case Manager can close' : ''}
              data-testid={`alert-status-${s.replace(' ', '-').toLowerCase()}`}
              className={`text-xs px-2 py-1 rounded-full border ${alert.status === s ? 'border-sky-400 bg-sky-50 text-sky-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
              {s}
            </button>
          );
        })}
      </div>
      {alert.closed_by_name && <p className="text-xs text-slate-400">Closed by {alert.closed_by_name} · {fmtDate(alert.closed_at)}</p>}
      <div className="space-y-3">
        {(alert.comments || []).map((c) => (
          <div key={c.id} className="flex gap-2" data-testid="alert-comment">
            <div className="h-7 w-7 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
              {(c.author_name || '?').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className="text-xs font-medium text-slate-800">{c.author_name}</span>
                <span className="text-[10px] text-slate-400">{fmtDate(c.created_at)}</span>
              </div>
              <Msg html={c.body_html} text={c.text} />
              <AttachmentChips caseId={caseId} attachments={c.attachments} />
            </div>
          </div>
        ))}
      </div>
      <RichComposer users={users} onSend={addComment} sending={sending} placeholder="Add a comment… use @ to mention" caseId={caseId} dataTestPrefix={`alert-${alert.id}`} />
    </div>
  );
}

export default CommunicationPanel;
